import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import type { LivePlayerEntry } from "./liveDirectoryTypes";

const RECENT_LAUNCH_MS = 4 * 60_000;

type LiveFindHandheldProps = {
  entries: LivePlayerEntry[];
  tournamentLabel: string;
};

const LiveFindHandheld: FC<LiveFindHandheldProps> = ({
  entries,
  tournamentLabel,
}) => {
  const [query, setQuery] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("fr");
    if (!q) return entries;
    return entries.filter((entry) => {
      const hay = `${entry.nom} ${entry.prenom}`.toLocaleLowerCase("fr");
      return hay.includes(q);
    });
  }, [entries, query]);

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        color: "#F4F7FB",
        background: `
          radial-gradient(900px 500px at 10% -10%, ${alpha("#1B6CA8", 0.4)} 0%, transparent 55%),
          radial-gradient(700px 400px at 90% 10%, ${alpha("#FF8A3D", 0.14)} 0%, transparent 50%),
          linear-gradient(165deg, #071018 0%, #0B1F33 48%, #10263A 100%)
        `,
      }}
    >
      <Stack
        spacing={1.75}
        sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 2, sm: 2.5 },
          pt: 2,
          pb: 2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: alpha("#F4F7FB", 0.5),
            }}
          >
            SQY Ping · Live
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Syne", sans-serif',
              fontWeight: 800,
              fontSize: { xs: "1.65rem", sm: "1.9rem" },
              letterSpacing: -1,
              lineHeight: 1.15,
              mt: 0.5,
              background: `linear-gradient(110deg, #F4F7FB 0%, #F4F7FB 55%, ${alpha("#FF8A3D", 0.95)} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: "transparent",
            }}
          >
            Où jouez-vous ?
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontFamily: '"Outfit", sans-serif',
              color: alpha("#F4F7FB", 0.55),
              fontSize: 13,
              lineHeight: 1.35,
            }}
          >
            {tournamentLabel}
            {" · "}
            {entries.length} joueur{entries.length > 1 ? "s" : ""} en match
          </Typography>
        </Box>

        <TextField
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Rechercher un joueur…"
          size="medium"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: alpha("#F4F7FB", 0.55) }} />
              </InputAdornment>
            ),
          }}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: alpha("#0B1F33", 0.92),
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              minHeight: 48,
              borderRadius: 2,
              color: "#F4F7FB",
              bgcolor: alpha("#fff", 0.06),
              "& fieldset": { borderColor: alpha("#fff", 0.14) },
              "&:hover fieldset": { borderColor: alpha("#fff", 0.28) },
              "&.Mui-focused fieldset": { borderColor: "#FF8A3D" },
            },
            "& .MuiInputBase-input::placeholder": {
              color: alpha("#F4F7FB", 0.45),
              opacity: 1,
            },
          }}
        />

        {entries.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              px: 2,
              py: 6,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Syne", sans-serif',
                  fontWeight: 800,
                  fontSize: "1.5rem",
                  letterSpacing: -0.4,
                }}
              >
                Aucun match en cours
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  color: alpha("#F4F7FB", 0.55),
                  fontFamily: '"Outfit", sans-serif',
                  fontSize: "1rem",
                }}
              >
                Les noms et n° de table apparaîtront ici automatiquement
              </Typography>
            </Box>
          </Box>
        ) : filtered.length === 0 ? (
          <Typography
            sx={{
              py: 4,
              textAlign: "center",
              color: alpha("#F4F7FB", 0.55),
              fontFamily: '"Outfit", sans-serif',
            }}
          >
            Aucun joueur ne correspond à « {query.trim()} »
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 1.25,
            }}
          >
            {filtered.map((entry) => {
              const isRecent =
                entry.launchedAtMs != null &&
                nowMs - entry.launchedAtMs >= 0 &&
                nowMs - entry.launchedAtMs <= RECENT_LAUNCH_MS;
              const accent = entry.boardColor || "#3D9BE8";
              const boardTitle = entry.boardLabel || entry.boardName || "";

              return (
                <Box
                  key={entry.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    minHeight: 56,
                    px: 1.75,
                    py: 1.25,
                    borderRadius: 2.5,
                    bgcolor: isRecent
                      ? alpha("#FF8A3D", 0.16)
                      : alpha("#fff", 0.05),
                    border: 1,
                    borderColor: isRecent
                      ? alpha("#FF8A3D", 0.7)
                      : alpha("#fff", 0.1),
                    boxShadow: isRecent
                      ? `0 0 0 1px ${alpha("#FF8A3D", 0.25)}`
                      : "none",
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      minWidth: 52,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Outfit", sans-serif',
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: 1.2,
                        textTransform: "uppercase",
                        color: alpha("#F4F7FB", 0.45),
                        lineHeight: 1.2,
                      }}
                    >
                      Table
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Syne", sans-serif',
                        fontWeight: 800,
                        fontSize: "1.75rem",
                        lineHeight: 1,
                        letterSpacing: -1,
                        color: "#FF8A3D",
                      }}
                    >
                      {entry.tableId}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Syne", sans-serif',
                        fontWeight: 800,
                        fontSize: "1.05rem",
                        letterSpacing: -0.3,
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {entry.nom}
                      {entry.prenom ? (
                        <Box
                          component="span"
                          sx={{
                            fontFamily: '"Outfit", sans-serif',
                            fontWeight: 600,
                            color: alpha("#F4F7FB", 0.75),
                            ml: 0.75,
                          }}
                        >
                          {entry.prenom}
                        </Box>
                      ) : null}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}
                    >
                      <Chip
                        size="small"
                        label={boardTitle || "—"}
                        sx={{
                          height: 24,
                          maxWidth: "100%",
                          fontWeight: 700,
                          fontSize: 11,
                          bgcolor: alpha(accent, 0.28),
                          color: "#F4F7FB",
                          border: `1px solid ${alpha(accent, 0.55)}`,
                          "& .MuiChip-label": {
                            px: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      />
                      {entry.tourLabel ? (
                        <Typography
                          sx={{
                            fontFamily: '"Outfit", sans-serif',
                            fontSize: 12,
                            color: alpha("#F4F7FB", 0.5),
                          }}
                        >
                          {entry.tourLabel}
                        </Typography>
                      ) : null}
                      {isRecent ? (
                        <Chip
                          size="small"
                          label="Nouveau"
                          sx={{
                            height: 22,
                            fontWeight: 800,
                            fontSize: 10,
                            letterSpacing: 0.6,
                            textTransform: "uppercase",
                            bgcolor: "#FF8A3D",
                            color: "#1A120C",
                          }}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default LiveFindHandheld;
